/**
 * ClientFinances - Page Finances Client (Redesign v2)
 * ====================================================
 * Séparation en 2 onglets : Contrats / Opérations Spot
 * - Contrats : Factures mensuelles groupées par contrat, expandable transferts
 * - Spot : Devis + factures d'opérations sans contrat
 * - Références cliquables (contrat → detail contrat, op → detail op)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FileText, Receipt, Download, Search, X, Check, 
  AlertCircle, RefreshCw, ChevronDown, ChevronRight,
  Clock, CreditCard, Wallet, CheckCircle, XCircle, FileDown, FileSpreadsheet, FolderOpen,
  MoreHorizontal, Eye, Link2, Building, Calendar, ChevronUp,
  Truck, Package, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { trackView } from '../utils/auditTrack';
import { useClientPermissions } from '../hooks/useClientPermissions';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { formatCurrency } from '../utils/formatters';
import EmptyState from '../components/ui-kit/EmptyState';
import { StatusLegendDrawer } from '../components/ui-kit/StatusBadge';
import MyStatementBanner from '../components/client/MyStatementBanner';
import { useExternalReference } from '../hooks/useExternalReference';
import DossierGroupedView from '../components/common/DossierGroupedView';

const token = 'cookie-auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// UTILITIES
// ============================================================

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '-'; }
};

const statusBadgeClass = (color) => ({
  green: 'bg-green-100 text-green-700 border-green-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  gray: 'bg-gray-100 text-gray-600 border-gray-200'
}[color] || 'bg-gray-100 text-gray-600 border-gray-200');

// ============================================================
// SUMMARY WIDGET
// ============================================================

const SummaryWidget = ({ title, count, amount, icon: Icon, color, onClick, active }) => (
  <Card 
    className={`cursor-pointer transition-all hover:shadow-md ${active ? 'ring-2 ring-emerald-500' : ''}`}
    onClick={onClick}
    data-testid={`widget-${title.replace(/\s+/g, '-').toLowerCase()}`}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-2xl font-bold mt-1 text-${color}-600`}>{count}</p>
          {amount !== undefined && amount > 0 && (
            <p className="text-sm text-gray-500 mt-1">{formatCurrency(amount)} FCFA</p>
          )}
        </div>
        <div className={`p-2 rounded-lg bg-${color}-50`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ============================================================
// CONTRACT INVOICE TRANSFERS (Expandable)
// ============================================================

const InvoiceTransfersExpander = ({ invoiceId }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadTransfers = async () => {
    if (loaded) { setOpen(!open); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/client/finances/contract-invoices/${invoiceId}/transfers`, {});
      setTransfers(res.data?.transfers || []);
      setLoaded(true);
      setOpen(true);
    } catch {
      toast({ title: "Erreur", description: "Impossible de charger les transferts", variant: "destructive" });
    }
    setLoading(false);
  };

  const goToTransfer = (t) => {
    if (t.kind === 'transfer' && t.contractId) {
      navigate(`/mes-contrats/${t.contractId}/transferts/${t.id}`);
    } else {
      navigate(`/operations/${t.id}`);
    }
  };

  return (
    <div>
      <button
        onClick={loadTransfers}
        className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium mt-1"
        data-testid={`expand-transfers-${invoiceId}`}
      >
        {loading ? (
          <RefreshCw className="w-3 h-3 animate-spin" />
        ) : open ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
        {open ? 'Masquer' : 'Voir'} les transferts inclus
      </button>
      {open && transfers.length > 0 && (
        <div className="mt-2 border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-1.5 text-left text-gray-500 font-medium">N° Op.</th>
                <th className="px-3 py-1.5 text-left text-gray-500 font-medium">Trajet</th>
                <th className="px-3 py-1.5 text-left text-gray-500 font-medium">Date</th>
                <th className="px-3 py-1.5 text-left text-gray-500 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((t, i) => (
                <tr key={t.id || i} className="border-t hover:bg-emerald-50/40">
                  <td className="px-3 py-1.5">
                    <button
                      type="button"
                      onClick={() => goToTransfer(t)}
                      className="text-emerald-700 hover:text-emerald-900 hover:underline font-medium inline-flex items-center gap-1"
                      data-testid={`link-transfer-${t.id}`}
                    >
                      {t.operationNumber}
                      <ArrowRight className="w-3 h-3 opacity-60" />
                    </button>
                  </td>
                  <td className="px-3 py-1.5 text-gray-600">{t.departure} <ArrowRight className="inline w-3 h-3" /> {t.arrival}</td>
                  <td className="px-3 py-1.5 text-gray-600">{formatDate(t.date)}</td>
                  <td className="px-3 py-1.5">
                    <Badge variant="outline" className="text-[10px] mr-1">
                      {t.kind === 'transfer' ? 'Transfert' : 'Spot'}
                    </Badge>
                    <Badge className="text-[10px] bg-gray-100 text-gray-600">{t.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && transfers.length === 0 && loaded && (
        <p className="text-xs text-gray-400 mt-2 italic">Aucun transfert trouvé pour cette période</p>
      )}
    </div>
  );
};

// ============================================================
// DETAIL MODAL
// ============================================================

const DetailModal = ({ document, open, onClose }) => {
  if (!document) return null;
  const isQuote = document.type === 'quote';
  const isContractInvoice = document.type === 'contract_invoice';
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isQuote ? <FileText className="w-5 h-5 text-blue-500" /> : 
             isContractInvoice ? <Receipt className="w-5 h-5 text-amber-500" /> :
             <Receipt className="w-5 h-5 text-purple-500" />}
            {document.typeLabel} {document.documentNumber}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            {isContractInvoice ? (
              <>
                <p className="text-sm text-gray-500 mb-1">Contrat</p>
                <p className="font-medium">{document.contractNumber}</p>
                {document.periodLabel && <p className="text-sm text-gray-600 mt-1">Période: {document.periodLabel}</p>}
                {document.transfersCount > 0 && <p className="text-sm text-amber-600 mt-1">{document.transfersCount} transfert(s) facturé(s)</p>}
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-1">Opération</p>
                <p className="font-medium">{document.operationNumber}</p>
                <p className="text-sm text-gray-600 mt-1">{document.departure} → {document.arrival}</p>
              </>
            )}
          </div>
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-sm text-emerald-700 font-medium mb-3">Résumé financier</p>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Montant total TTC</span>
                <span className="font-medium">{formatCurrency(document.totalAmount)} FCFA</span>
              </div>
              {!isQuote && (
                <>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Montant réglé</span>
                    <span className="font-medium text-green-600">{formatCurrency(document.paidAmount)} FCFA</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-medium">Reste à payer</span>
                    <span className={`font-bold ${document.remainingAmount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {formatCurrency(document.remainingAmount)} FCFA
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-sm text-gray-500">Date d'émission</p><p className="font-medium">{formatDate(document.issuedAt)}</p></div>
            <div><p className="text-sm text-gray-500">Échéance</p><p className="font-medium">{formatDate(document.dueDate)}</p></div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Statut</p>
            <div className="flex items-center gap-2 flex-wrap">
            <Badge className={statusBadgeClass(document.statusColor)}>{document.statusLabel}</Badge>
            {document.fneCertified && (
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200" title={`Certifiée FNE (DGI)${document.fneReference ? ' — Réf. ' + document.fneReference : ''}`} data-testid="fne-badge">
                FNE certifiée
              </Badge>
            )}
          </div>
          </div>
          {document.wavePaymentLink && (
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-700 font-medium mb-2">Lien de paiement Wave</p>
              <div className="flex items-center gap-2">
                <input type="text" value={document.wavePaymentLink} readOnly className="flex-1 text-sm bg-white border rounded px-2 py-1" />
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(document.wavePaymentLink); toast({ title: "Lien copié !" }); }}>Copier</Button>
                <Button size="sm" asChild><a href={document.wavePaymentLink} target="_blank" rel="noopener noreferrer">Payer</a></Button>
              </div>
            </div>
          )}
          {document.payments?.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 mb-2">Historique des paiements</p>
              <div className="space-y-2">
                {document.payments.map((p, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                    <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm">{formatDate(p.date)}</span></div>
                    <span className="text-sm font-medium">{formatCurrency(p.amount)} FCFA</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// CONFIRM MODAL
// ============================================================

const ConfirmModal = ({ open, onClose, onConfirm, title, description, confirmText, variant = 'default' }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Annuler</Button>
        <Button onClick={onConfirm} variant={variant === 'destructive' ? 'destructive' : 'default'}
          className={variant === 'success' ? 'bg-green-600 hover:bg-green-700' : ''}>
          {confirmText}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ============================================================
// SPOT DOCUMENT ROW
// ============================================================

const SpotDocumentRow = ({ document, selected, onSelect, onAcceptQuote, onRejectQuote, onViewDetails, onDownloadPdf, navigate, showExternalRef }) => {
  const isQuote = document.type === 'quote';
  return (
    <tr className={`border-b hover:bg-gray-50 ${selected ? 'bg-emerald-50' : ''}`}>
      <td className="px-4 py-3"><Checkbox checked={selected} onCheckedChange={() => onSelect(document.id)} /></td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {isQuote ? <FileText className="w-4 h-4 text-blue-500" /> : <Receipt className="w-4 h-4 text-purple-500" />}
          <span className={`text-xs font-medium ${isQuote ? 'text-blue-600' : 'text-purple-600'}`}>{document.typeLabel}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <button onClick={() => navigate(`/operations/${document.operationId}`)}
          className="text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
          data-testid={`link-op-${document.operationId}`}>
          {document.operationNumber || 'N/A'}
        </button>
      </td>
      {showExternalRef && (
        <td className="px-4 py-3" data-testid={`ext-ref-cell-${document.id}`}>
          {document.externalRef
            ? <span className="text-sm font-mono text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{document.externalRef}</span>
            : <span className="text-sm text-gray-300">—</span>}
        </td>
      )}
      <td className="px-4 py-3"><span className="text-sm text-gray-600">{document.documentNumber}</span></td>
      <td className="px-4 py-3"><span className="text-sm text-gray-700">{(document.departure || '').split(',')[0]} → {(document.arrival || '').split(',')[0]}</span></td>
      <td className="px-4 py-3 text-right"><span className="text-sm font-medium">{formatCurrency(document.totalAmount)} FCFA</span></td>
      <td className="px-4 py-3 text-right">
        {isQuote ? <span className="text-sm text-gray-400">-</span> : <span className="text-sm text-green-600">{formatCurrency(document.paidAmount)} FCFA</span>}
      </td>
      <td className="px-4 py-3 text-right">
        {isQuote ? <span className="text-sm text-gray-400">-</span> : (
          <span className={`text-sm font-medium ${document.remainingAmount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
            {formatCurrency(document.remainingAmount)} FCFA
          </span>
        )}
      </td>
      <td className="px-4 py-3"><span className="text-sm text-gray-600">{formatDate(document.issuedAt)}</span></td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 flex-wrap">
          <Badge className={`text-xs ${statusBadgeClass(document.statusColor)}`}>{document.statusLabel}</Badge>
          {document.fneCertified && (
            <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200" title={`Certifiée FNE (DGI)${document.fneReference ? ' — Réf. ' + document.fneReference : ''}`} data-testid="fne-badge">FNE</Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onDownloadPdf(document)}
            title="Télécharger le PDF"
            data-testid={`download-pdf-${document.id}`}
          >
            <Download className="w-4 h-4" />
          </Button>
          <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onViewDetails(document)}><Eye className="w-4 h-4 mr-2" />Voir détails</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDownloadPdf(document)}><Download className="w-4 h-4 mr-2" />Télécharger PDF</DropdownMenuItem>
            {isQuote && document.canAccept && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onAcceptQuote(document)} className="text-green-600"><Check className="w-4 h-4 mr-2" />Accepter le devis</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRejectQuote(document)} className="text-red-600"><X className="w-4 h-4 mr-2" />Refuser le devis</DropdownMenuItem>
              </>
            )}
            {!isQuote && document.wavePaymentLink && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href={document.wavePaymentLink} target="_blank" rel="noopener noreferrer"><Wallet className="w-4 h-4 mr-2" />Payer via Wave</a>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </td>
    </tr>
  );
};

// ============================================================
// CONTRACT CARD (Grouped invoices)
// ============================================================

const ContractGroup = ({ contract, onViewDetails, onDownloadPdf, navigate }) => {
  const [expanded, setExpanded] = useState(true);
  const invoices = contract.invoices || [];
  const statusColor = contract.contractStatus === 'active' ? 'emerald' : 'gray';

  return (
    <Card className="overflow-hidden" data-testid={`contract-group-${contract.contractId}`}>
      {/* Contract Header */}
      <div className="bg-gradient-to-r from-slate-50 to-white px-5 py-4 border-b cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <Building className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={(e) => { e.stopPropagation(); navigate(`/client/contracts/${contract.contractId}`); }}
                  className="text-sm font-semibold text-amber-700 hover:text-amber-900 hover:underline"
                  data-testid={`link-contract-${contract.contractId}`}
                >
                  {contract.contractNumber}
                </button>
                <Badge className={`text-[10px] bg-${statusColor}-100 text-${statusColor}-700 border-${statusColor}-200`}>
                  {contract.contractStatus === 'active' ? 'Actif' : contract.contractStatus}
                </Badge>
              </div>
              {contract.contractName && <p className="text-xs text-gray-500">{contract.contractName}</p>}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-xs text-gray-400">Montant mensuel</p>
              <p className="text-sm font-semibold text-gray-800">{formatCurrency(contract.monthlyAmount)} FCFA</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">{invoices.length} facture(s)</p>
              {contract.totalRemaining > 0 && (
                <p className="text-sm font-semibold text-orange-600">{formatCurrency(contract.totalRemaining)} XOF restant</p>
              )}
            </div>
            {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
      </div>
      
      {/* Invoices List */}
      {expanded && (
        <div className="divide-y">
          {invoices.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">Aucune facture émise pour ce contrat</div>
          ) : invoices.map(inv => (
            <div key={inv.id} className="px-5 py-3 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <Receipt className="w-4 h-4 text-amber-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{inv.documentNumber || 'N/A'}</span>
                      {inv.periodLabel && <span className="text-xs text-gray-500">({inv.periodLabel})</span>}
                    </div>
                    {inv.transfersCount > 0 && (
                      <span className="text-xs text-gray-500">{inv.transfersCount} transfert(s)</span>
                    )}
                    {/* Expandable transfers */}
                    <InvoiceTransfersExpander invoiceId={inv.id?.replace('contract_invoice_', '')} />
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-medium">{formatCurrency(inv.totalAmount)} XOF</p>
                    {inv.remainingAmount > 0 && (
                      <p className="text-xs text-orange-600">Reste: {formatCurrency(inv.remainingAmount)} XOF</p>
                    )}
                  </div>
                  <div className="text-right w-20">
                    <Badge className={`text-[10px] ${statusBadgeClass(inv.statusColor)}`}>{inv.statusLabel}</Badge>
                    {inv.fneCertified && (
                      <Badge className="mt-1 text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200" title={`Certifiée FNE (DGI)${inv.fneReference ? ' — Réf. ' + inv.fneReference : ''}`} data-testid="fne-badge">FNE</Badge>
                    )}
                  </div>
                  <span className="text-xs text-gray-500 w-20">{formatDate(inv.issuedAt)}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onDownloadPdf(inv)}
                    title="Télécharger la facture"
                    data-testid={`download-invoice-${inv.id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="w-3.5 h-3.5" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onViewDetails(inv)}><Eye className="w-4 h-4 mr-2" />Voir détails</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onDownloadPdf(inv)}><Download className="w-4 h-4 mr-2" />Télécharger PDF</DropdownMenuItem>
                      {inv.wavePaymentLink && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <a href={inv.wavePaymentLink} target="_blank" rel="noopener noreferrer"><Wallet className="w-4 h-4 mr-2" />Payer via Wave</a>
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const ClientFinances = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const permissions = useClientPermissions();
  const { config: extRefConfig } = useExternalReference();

  // PM Fix 17/02/2026 — Tous les hooks DOIVENT être appelés avant tout early return
  // (rules-of-hooks). Le check de permission devient un render conditionnel à la fin.
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'spot');
  const [documents, setDocuments] = useState([]);
  const [contractsData, setContractsData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [locations, setLocations] = useState({ departures: [], arrivals: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filters, setFilters] = useState({
    operationNumber: searchParams.get('operation') || '',
    type: '', status: '', departure: '', arrival: '',
    minAmount: '', maxAmount: '', dateFrom: '', dateTo: ''
  });
  const [detailDocument, setDetailDocument] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeWidget, setActiveWidget] = useState(null);

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: 'Bearer cookie-auth' } : {};
  }, []);

  // ============================================================
  // DATA FETCHING
  // ============================================================

  const fetchDocuments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('tab', activeTab);
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.operationNumber) params.append('operation_number', filters.operationNumber);
      if (filters.departure) params.append('departure', filters.departure);
      if (filters.arrival) params.append('arrival', filters.arrival);
      if (filters.minAmount) params.append('min_amount', filters.minAmount);
      if (filters.maxAmount) params.append('max_amount', filters.maxAmount);
      
      const response = await axios.get(`${API}/client/finances?${params}`, { headers: getAuthHeaders() });
      
      if (activeTab === 'contracts') {
        const data = response.data || {};
        setContractsData(data.contracts || []);
        setDocuments(data.documents || []);
      } else {
        setDocuments(response.data || []);
        setContractsData([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Erreur lors du chargement des documents');
    }
  }, [activeTab, filters, getAuthHeaders]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/client/finances/summary?tab=${activeTab}`, { headers: getAuthHeaders() });
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, [activeTab, getAuthHeaders]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/client/finances/locations`, { headers: getAuthHeaders() });
      setLocations(response.data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDocuments(), fetchSummary(), activeTab === 'spot' ? fetchLocations() : Promise.resolve()]);
      setLoading(false);
    };
    loadData();
    setSelectedIds(new Set());
    setActiveWidget(null);
  }, [fetchDocuments, fetchSummary, fetchLocations, activeTab]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilters({ operationNumber: '', type: '', status: '', departure: '', arrival: '', minAmount: '', maxAmount: '', dateFrom: '', dateTo: '' });
    setSearchParams({ tab });
  };

  const handleWidgetClick = (widgetType) => {
    if (activeWidget === widgetType) {
      setActiveWidget(null);
      setFilters(prev => ({ ...prev, type: '', status: '' }));
    } else {
      setActiveWidget(widgetType);
      switch (widgetType) {
        case 'quotes_pending': setFilters(prev => ({ ...prev, type: 'quote', status: 'pending_validation' })); break;
        case 'quotes_accepted': setFilters(prev => ({ ...prev, type: 'quote', status: 'accepted' })); break;
        case 'invoices_pending': setFilters(prev => ({ ...prev, type: activeTab === 'contracts' ? 'contract_invoice' : 'invoice', status: 'pending' })); break;
        case 'invoices_paid': setFilters(prev => ({ ...prev, type: activeTab === 'contracts' ? 'contract_invoice' : 'invoice', status: 'paid' })); break;
        default: break;
      }
    }
  };

  const handleSelectDocument = (id) => {
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const handleSelectAll = () => {
    selectedIds.size === documents.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(documents.map(d => d.id)));
  };

  const handleAcceptQuote = (document) => {
    setConfirmAction({
      type: 'accept', document,
      title: 'Accepter le devis',
      description: `Accepter ce devis de ${formatCurrency(document.totalAmount)} FCFA pour ${document.operationNumber} ?`,
      confirmText: 'Accepter le devis', variant: 'success'
    });
  };

  const handleRejectQuote = (document) => {
    setConfirmAction({
      type: 'reject', document,
      title: 'Refuser le devis',
      description: `Refuser ce devis pour ${document.operationNumber} ?`,
      confirmText: 'Refuser le devis', variant: 'destructive'
    });
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    try {
      const { type, document } = confirmAction;
      if (type === 'accept') {
        await axios.put(`${API}/client/finances/quotes/${document.operationId}/accept`, null, { headers: getAuthHeaders() });
        try { (await import('../utils/analytics')).trackFeature('quote.accept', { amount: document.totalAmount }); } catch (_) {}
        toast({ title: "Devis accepté", description: "L'opération va être planifiée." });
      } else if (type === 'reject') {
        await axios.put(`${API}/client/finances/quotes/${document.operationId}/reject`, {}, { headers: getAuthHeaders() });
        try { (await import('../utils/analytics')).trackFeature('quote.reject', { amount: document.totalAmount }); } catch (_) {}
        toast({ title: "Devis refusé" });
      }
      await Promise.all([fetchDocuments(), fetchSummary()]);
    } catch (err) {
      toast({ title: "Erreur", description: err.response?.data?.detail || "Une erreur est survenue", variant: "destructive" });
    } finally { setConfirmAction(null); }
  };

  const handleDownloadPdf = async (document) => {
    try {
      toast({ title: "Génération en cours..." });

      // Track feature usage (Phase 3) — distingue facture vs relevé vs autre
      try {
        const { trackFeature } = await import('../utils/analytics');
        if (document.type === 'contract_invoice' || document.type === 'spot_invoice' || document.type === 'invoice') {
          trackFeature('invoice.download', { documentId: document.id, type: document.type });
        } else if (document.type === 'statement' || document.type === 'monthly_statement') {
          trackFeature('statement.download', { documentId: document.id });
        }
      } catch (_) {}

      // Déterminer si c'est une facture contrat ou spot
      const isContractInvoice = document.type === 'contract_invoice';

      if (isContractInvoice) {
        // Facture contrat → endpoint dédié
        const invoiceId = (document.id || '').replace('contract_invoice_', '');
        const response = await axios.get(`${API}/contract-invoices/${invoiceId}/download`, {
          headers: getAuthHeaders(), responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = window.document.createElement('a');
        link.href = url;
        link.download = `Facture_${document.documentNumber || invoiceId}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        // Facture/devis spot → endpoint client dédié (vérifie ownership, réutilise PDFService)
        // Bug fix 01/06/2026 : utilisait `/documents/generate/invoice/...` qui exige une perm staff (403).
        const response = await axios.get(`${API}/client/finances/documents/${document.id}/pdf`, {
          headers: getAuthHeaders(), responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = window.document.createElement('a');
        link.href = url;
        const label = document.type === 'quote' ? 'Devis' : 'Facture';
        link.download = `${label}_${document.documentNumber || document.operationNumber}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      }

      toast({ title: "Document téléchargé" });
    } catch {
      toast({ title: "Erreur", description: "Impossible de télécharger le document", variant: "destructive" });
    }
  };

  // Traçabilité : consultation d'un devis / d'une facture (journal d'audit)
  const handleViewDetails = (doc) => {
    const isQuote = (doc?.type || '').includes('quote');
    trackView(isQuote ? 'VIEW_QUOTE' : 'VIEW_INVOICE', {
      resourceType: 'documents',
      resourceId: doc?.id,
      details: {
        documentNumber: doc?.documentNumber,
        documentType: doc?.type,
        operationNumber: doc?.operationNumber,
        amount: doc?.amount,
      },
    });
    setDetailDocument(doc);
  };

  const handleExportBulk = async (format) => {
    // Règle produit : l'export porte sur toute la vue affichée ; s'il y a des
    // lignes cochées, il se limite à cette sélection. Le ZIP de PDF reste
    // volontairement limité à une sélection explicite.
    const displayedIds = documents.map(d => d.id);
    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : displayedIds;
    if (format === 'zip' && selectedIds.size === 0) {
      toast({ title: "Sélectionnez les documents à télécharger", variant: "destructive" });
      return;
    }
    if (ids.length === 0) { toast({ title: "Aucun document à exporter", variant: "destructive" }); return; }
    try {
      const response = await axios.post(`${API}/client/finances/export`, {
        document_ids: ids, format
      }, { headers: getAuthHeaders(), responseType: 'blob' });
      const filename = format === 'xlsx'
        ? `BaobabConnect_MesFinances_${new Date().toISOString().split('T')[0]}.xlsx`
        : (format === 'excel' ? 'finances_export.csv' : 'documents_export.zip');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      toast({ title: "Export téléchargé", description: selectedIds.size > 0 ? `${ids.length} document(s) sélectionné(s)` : `${ids.length} document(s) affiché(s)` });
    } catch {
      toast({ title: "Erreur", description: "Impossible d'exporter", variant: "destructive" });
    }
  };

  const handleResetFilters = () => {
    setFilters({ operationNumber: '', type: '', status: '', departure: '', arrival: '', minAmount: '', maxAmount: '', dateFrom: '', dateTo: '' });
    setActiveWidget(null);
  };

  // ============================================================
  // RENDER
  // ============================================================

  // PM Fix 17/02/2026 — Render conditionnel APRÈS tous les hooks (rules-of-hooks)
  if (!permissions.canViewFinances) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center" data-testid="finances-access-denied">
        <XCircle className="w-16 h-16 text-red-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès restreint</h2>
        <p className="text-gray-500 max-w-md">Votre profil logistique ne vous donne pas accès aux données financières.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="client-finances-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">Mes Finances</h1>
          <p className="text-sm text-slate-500 mt-0.5">Gérez vos devis et factures</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusLegendDrawer />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline"><Download className="w-4 h-4 mr-2" />Exporter<ChevronDown className="w-4 h-4 ml-2" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs text-gray-500">
                {selectedIds.size > 0 ? `${selectedIds.size} ligne(s) sélectionnée(s)` : `Vue affichée — ${documents.length} document(s)`}
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleExportBulk('xlsx')} data-testid="export-finances-xlsx-btn"><FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />Exporter en Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportBulk('excel')} data-testid="export-finances-csv-btn"><FileText className="w-4 h-4 mr-2" />Exporter en CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportBulk('zip')} data-testid="export-finances-zip-btn"><FileDown className="w-4 h-4 mr-2" />Télécharger les PDF sélectionnés (ZIP)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Canal B — Bandeau "Mon relevé de compte" (self-service) */}
      <MyStatementBanner />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className={`grid w-full ${extRefConfig.groupingEnabled ? 'max-w-2xl grid-cols-3' : 'max-w-md grid-cols-2'}`} data-testid="finances-tabs">
          <TabsTrigger value="spot" className="flex items-center gap-2" data-testid="tab-spot">
            <Truck className="w-4 h-4" /> Opérations Spot
          </TabsTrigger>
          {extRefConfig.groupingEnabled && (
            <TabsTrigger value="dossiers" className="flex items-center gap-2" data-testid="tab-dossiers">
              <FolderOpen className="w-4 h-4" /> Par {extRefConfig.label || 'dossier'}
            </TabsTrigger>
          )}
          <TabsTrigger value="contracts" className="flex items-center gap-2" data-testid="tab-contracts">
            <Building className="w-4 h-4" /> Contrats
          </TabsTrigger>
        </TabsList>

        {/* ============================================================ */}
        {/* SPOT TAB */}
        {/* ============================================================ */}
        <TabsContent value="spot" className="space-y-6 mt-4">
          {/* Widgets Spot */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryWidget title="Devis en attente" count={summary?.quotesAwaitingValidation || 0} icon={Clock} color="yellow"
              onClick={() => handleWidgetClick('quotes_pending')} active={activeWidget === 'quotes_pending'} />
            <SummaryWidget title="Devis acceptés" count={summary?.quotesAccepted || 0} amount={summary?.quotesAcceptedAmount} icon={CheckCircle} color="green"
              onClick={() => handleWidgetClick('quotes_accepted')} active={activeWidget === 'quotes_accepted'} />
            <SummaryWidget title="Factures à payer" count={(summary?.invoicesPending || 0) + (summary?.invoicesPartial || 0) + (summary?.invoicesOverdue || 0)}
              amount={summary?.invoicesPendingAmount} icon={CreditCard} color="orange"
              onClick={() => handleWidgetClick('invoices_pending')} active={activeWidget === 'invoices_pending'} />
            <SummaryWidget title="Factures payées" count={summary?.invoicesPaid || 0} amount={summary?.invoicesPaidAmount} icon={Wallet} color="emerald"
              onClick={() => handleWidgetClick('invoices_paid')} active={activeWidget === 'invoices_paid'} />
          </div>

          {/* Filters Spot — sticky desktop */}
          <Card className="lg:sticky lg:top-2 lg:z-20 lg:bg-white/95 lg:backdrop-blur-sm lg:shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" />Recherche</CardTitle>
                <Button variant="ghost" size="sm" onClick={handleResetFilters}><X className="w-4 h-4 mr-1" />Réinitialiser</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">N° Opération</label>
                  <Input placeholder="BAO-OP-..." value={filters.operationNumber}
                    onChange={(e) => setFilters(prev => ({ ...prev, operationNumber: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Type</label>
                  <Select value={filters.type || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, type: v === "all" ? "" : v }))}>
                    <SelectTrigger><SelectValue placeholder="Tous" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="quote">Devis</SelectItem>
                      <SelectItem value="invoice">Factures</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Statut</label>
                  <Select value={filters.status || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
                    <SelectTrigger><SelectValue placeholder="Tous" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="pending_validation">En attente validation</SelectItem>
                      <SelectItem value="accepted">Accepté</SelectItem>
                      <SelectItem value="rejected">Refusé</SelectItem>
                      <SelectItem value="pending">À payer</SelectItem>
                      <SelectItem value="partial">Partiellement payé</SelectItem>
                      <SelectItem value="paid">Payé</SelectItem>
                      <SelectItem value="overdue">En retard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Montant min/max (XOF)</label>
                  <div className="flex gap-2">
                    <Input type="number" placeholder="Min" value={filters.minAmount}
                      onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))} className="flex-1" />
                    <Input type="number" placeholder="Max" value={filters.maxAmount}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))} className="flex-1" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents List Spot */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Checkbox checked={selectedIds.size > 0 && selectedIds.size === documents.length} onCheckedChange={handleSelectAll} />
                  <span className="text-sm text-gray-500">{selectedIds.size > 0 ? `${selectedIds.size} sélectionné(s)` : 'Tout sélectionner'}</span>
                </div>
                {selectedIds.size > 0 && (
                  <Button variant="outline" size="sm" onClick={() => handleExportBulk('zip')}>
                    <Download className="w-4 h-4 mr-2" />Télécharger sélection
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {error ? (
                <div className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
                  <p className="text-red-600 mb-4">{error}</p>
                  <Button variant="outline" onClick={() => { fetchDocuments(); fetchSummary(); }}><RefreshCw className="w-4 h-4 mr-2" />Réessayer</Button>
                </div>
              ) : documents.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">Aucun document trouvé pour les opérations spot</p>
                  {(filters.type || filters.status || filters.operationNumber) && (
                    <Button variant="link" onClick={handleResetFilters}>Réinitialiser les filtres</Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"><Checkbox checked={selectedIds.size > 0 && selectedIds.size === documents.length} onCheckedChange={handleSelectAll} /></th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Opération</th>
                        {extRefConfig.enabled && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-testid="ext-ref-column-header">{extRefConfig.label}</th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Doc</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trajet</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Réglé</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reste</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map(doc => (
                        <SpotDocumentRow key={doc.id} document={doc} selected={selectedIds.has(doc.id)}
                          onSelect={handleSelectDocument} onAcceptQuote={handleAcceptQuote} onRejectQuote={handleRejectQuote}
                          onViewDetails={handleViewDetails} onDownloadPdf={handleDownloadPdf} navigate={navigate}
                          showExternalRef={extRefConfig.enabled} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================================================ */}
        {/* CONTRACTS TAB */}
        {/* ============================================================ */}
        {/* ===== ONGLET PAR DOSSIER (LOT B) ===== */}
        {extRefConfig.groupingEnabled && (
          <TabsContent value="dossiers" className="space-y-6 mt-4">
            <DossierGroupedView />
          </TabsContent>
        )}


        <TabsContent value="contracts" className="space-y-6 mt-4">
          {/* Widgets Contracts */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryWidget title="Contrats actifs" count={contractsData.filter(c => c.contractStatus === 'active').length} icon={Building} color="emerald" />
            <SummaryWidget title="Factures à payer" count={summary?.contractInvoicesPending || 0}
              amount={summary?.contractInvoicesPendingAmount} icon={CreditCard} color="orange"
              onClick={() => handleWidgetClick('invoices_pending')} active={activeWidget === 'invoices_pending'} />
            <SummaryWidget title="Factures payées" count={summary?.contractInvoicesPaid || 0}
              amount={summary?.contractInvoicesPaidAmount} icon={Wallet} color="emerald"
              onClick={() => handleWidgetClick('invoices_paid')} active={activeWidget === 'invoices_paid'} />
            <SummaryWidget title="Total facturé"
              count={contractsData.reduce((s, c) => s + (c.invoicesCount || 0), 0)}
              amount={contractsData.reduce((s, c) => s + (c.totalInvoiced || 0), 0)}
              icon={Receipt} color="blue" />
          </div>

          {/* Contract Groups */}
          {error ? (
            <Card>
              <CardContent className="p-8 text-center">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
                <p className="text-red-600 mb-4">{error}</p>
                <Button variant="outline" onClick={() => { fetchDocuments(); fetchSummary(); }}><RefreshCw className="w-4 h-4 mr-2" />Réessayer</Button>
              </CardContent>
            </Card>
          ) : contractsData.length === 0 ? (
            <Card>
              <CardContent className="p-0">
                <EmptyState
                  icon={Building}
                  title="Aucun contrat trouvé"
                  description="Vous n'avez pas encore de contrat actif chez Baobab Connect."
                  variant="compact"
                />
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {contractsData.map(contract => (
                <ContractGroup key={contract.contractId} contract={contract}
                  onViewDetails={handleViewDetails} onDownloadPdf={handleDownloadPdf} navigate={navigate} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      <DetailModal document={detailDocument} open={!!detailDocument} onClose={() => setDetailDocument(null)} />

      {/* Confirm Modal */}
      {confirmAction && (
        <ConfirmModal open={!!confirmAction} onClose={() => setConfirmAction(null)} onConfirm={executeConfirmAction}
          title={confirmAction.title} description={confirmAction.description}
          confirmText={confirmAction.confirmText} variant={confirmAction.variant} />
      )}
    </div>
  );
};

export default ClientFinances;
